import React, { useRef, useState } from "react";
import Draggable from "react-draggable";
import { ResizableBox, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";
import styles from "./AnimationLegend.module.scss";

interface FrameRange {
  start: number;
  end: number;
}

interface StateDependencies {
  views?: string[];
  hotspots?: Array<{ id: string }>;
  customTriggers?: string[];
}

interface AnimationStateData {
  frameRange?: FrameRange;
  customTrigger?: string;
  altText: string;
  state_dependencies?: StateDependencies;
}

interface Animation {
  entityId: string;
  currentStateIndex: number;
  totalStates: number;
  stateData: AnimationStateData;
}

interface AnimationLegendEntryProps {
  animation: Animation;
  color: string;
  isHidden: boolean;
  isContentHidden: boolean;
  onToggle: (entityId: string, stateIndex: number) => void;
  onToggleContent: (entityId: string, stateIndex: number) => void;
}

const AnimationLegendEntry: React.FC<AnimationLegendEntryProps> = ({
  animation,
  color,
  isHidden,
  isContentHidden,
  onToggle,
  onToggleContent,
}) => {
  const { entityId, currentStateIndex, totalStates, stateData } = animation;

  return (
    <div className={styles.legendEntry}>
      <div className={styles.entryHeader}>
        <div
          className={styles.colorSwatch}
          style={{ backgroundColor: color }}
        />
        <strong>Anim {entityId}</strong>
        <button onClick={() => onToggleContent(entityId, currentStateIndex)}>
          {isContentHidden ? "🚫" : "✅"}
        </button>
        <button onClick={() => onToggle(entityId, currentStateIndex)}>
          {isHidden ? "🔴" : "🟢"}
        </button>
      </div>

      <div className={styles.entryDetails}>
        <div className={styles.detailRow}>
          <span className={styles.label}>State:</span>
          <span className={styles.value}>
            {currentStateIndex + 1} of {totalStates}
          </span>
        </div>

        {stateData.frameRange && (
          <div className={styles.detailRow}>
            <span className={styles.label}>Frames:</span>
            <span className={styles.value}>
              {stateData.frameRange.start}-{stateData.frameRange.end}
            </span>
          </div>
        )}

        {stateData.customTrigger && (
          <div className={styles.detailRow}>
            <span className={styles.label}>Trigger:</span>
            <span className={`${styles.value} ${styles.code}`}>
              {stateData.customTrigger}
            </span>
          </div>
        )}

        <div className={styles.detailRow} style={{ display: "block" }}>
          <span className={styles.label}>Alt:</span>
          <div className={styles.value} style={{ marginTop: "2px" }}>
            {stateData.altText}
          </div>
        </div>

        {stateData.state_dependencies && (
          <div className={styles.dependencies}>
            <div className={styles.depLabel}>Dependencies:</div>
            {stateData.state_dependencies.views &&
              stateData.state_dependencies.views.length > 0 && (
                <div className={styles.depItem}>
                  • Views: [{stateData.state_dependencies.views.join(", ")}]
                </div>
              )}
            {stateData.state_dependencies.hotspots &&
              stateData.state_dependencies.hotspots.length > 0 && (
                <div className={styles.depItem}>
                  • Hotspots:{" "}
                  {stateData.state_dependencies.hotspots
                    .map((h) => h.id)
                    .join(", ")}
                </div>
              )}
            {stateData.state_dependencies.customTriggers &&
              stateData.state_dependencies.customTriggers.length > 0 && (
                <div className={styles.depItem}>
                  • Triggers:{" "}
                  {stateData.state_dependencies.customTriggers.join(", ")}
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

interface AnimationLegendProps {
  activeAnimations: Animation[];
  colorMap: Map<string, string>;
  hiddenAnimations: Set<string>;
  hiddenAnimationContent: Set<string>;
  onToggleHighlight: (entityId: string, stateIndex: number) => void;
  onToggleContent: (entityId: string, stateIndex: number) => void;
}

export const AnimationLegend: React.FC<AnimationLegendProps> = ({
  activeAnimations,
  colorMap,
  hiddenAnimations,
  hiddenAnimationContent,
  onToggleHighlight,
  onToggleContent,
}) => {
  const [size, setSize] = useState({ width: 320, height: 400 });
  const [isResizing, setIsResizing] = useState(false);
  const legendRef = useRef<HTMLDivElement>(null);

  return (
    <Draggable
      handle={`.${styles.dragHandle}`}
      defaultPosition={{ x: 10, y: 10 }}
      disabled={isResizing}
    >
      <div
        className={styles.legendContainer}
        ref={legendRef}
        style={{
          top: 0,
          left: 0,
          width: `${size.width}px`,
          height: `${size.height}px`,
        }}
      >
        <ResizableBox
          width={size.width}
          height={size.height}
          minConstraints={[250, 200]}
          maxConstraints={[
            typeof window !== "undefined" ? window.innerWidth : 1920,
            typeof window !== "undefined" ? window.innerHeight : 1080,
          ]}
          resizeHandles={["se"]}
          onResizeStart={() => setIsResizing(true)}
          onResize={(_e: React.SyntheticEvent, data: ResizeCallbackData) => {
            setSize({
              width: data.size.width,
              height: data.size.height,
            });
          }}
          onResizeStop={() => setIsResizing(false)}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className={styles.dragHandle}>
              <h3>Animations</h3>
              <span className={styles.dragIcon} aria-label="Drag to move">
                ⠿
              </span>
            </div>

            <div className={styles.legendContent}>
              {activeAnimations.length === 0 ? (
                <div className={styles.emptyState}>
                  No active animations in current view
                </div>
              ) : (
                activeAnimations.map((anim) => {
                  const colorKey = `${anim.entityId}-${anim.currentStateIndex}`;
                  const color = colorMap.get(colorKey) || "#999";
                  const isHidden = hiddenAnimations.has(colorKey);
                  const isContentHidden = hiddenAnimationContent.has(colorKey);

                  return (
                    <AnimationLegendEntry
                      key={colorKey}
                      animation={anim}
                      color={color}
                      isHidden={isHidden}
                      isContentHidden={isContentHidden}
                      onToggle={onToggleHighlight}
                      onToggleContent={onToggleContent}
                    />
                  );
                })
              )}
            </div>
          </div>
        </ResizableBox>
      </div>
    </Draggable>
  );
};
